import React, { useEffect } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import Popup from 'semantic-ui-react/dist/commonjs/modules/Popup';

import {
  ModalBodyContainer,
  ModalHeader,
  ModalSubHeader,
  StatusContainer,
  StatusItem,
  HelpSectionContainer,
  ModalContainer,
  CloseModalIcon
} from '../utils-transaction-history/styles';
import {
  RFI_DRAFT,
  REFUND_STATES,
  PAY_IN_STATES,
  INCOMPLETE_REFUND_STATES,
  TRANSFER_FUNDED,
  CASH_PAYOUT,
  PROCESSING,
  AT_RECEPIENT_BANK,
  TRANSFER_CREATED,
  TRANSACTION_FINAL_STATES,
  RFI_URL,
  HELP_CENTER,
  HISTORY_URL,
  TRANSACTION_STAGES_NO_TIMELINE,
  AWAITING_PAYMENT,
  PAID,
  PROCESSED_BY_CLEARING,
  SENT_TO_BENEFICIARY_BANK,
  DEEMED_PAID_TIMELINE_TEXT
} from '../../utils/constants';
import { getLocaleSpecificTime } from '../library';
import CommonButton from '../../components/common-button/CommonButton';
import { TransferStatus, ITransactionTimeLine, GenericObject } from './type';
import { EventNames, FLOW_TYPE, LOG_TYPE } from '../../utils/events/eventsConstants';
import { ExistingTicketModal } from '../../screens/help-center/components/raise-enquiry/ExistingTicketModal';
import { logEvent } from '../events/logUserEvents';
import {
  CLOSE_HEAVY_ICON,
  PAID_TRANSACTION_ICON,
  QUESTION_MARK_ICON,
  SOLID_VERTICAL_LINE_ICON,
  STROKED_VERTICAL_LINE_ICON,
  TRANSACTION_TIMELINE_ACTIVE_ICON,
  TRANSACTION_TIMELINE_DISABLE_ICON,
  TRANSACTION_TIMELINE_RFI_RAISED_ICON
} from '@Images';
import { HelpCenterDeepLink } from '../../components/deeplink-help-center/HelpCenterDeepLink';

const getPayInLabel = (payInTime: string, payInLabel: string) => {
  const currentUtc = moment.utc();
  const upcomingUtc = moment.utc(payInTime);
  const diffInDays = upcomingUtc?.diff(currentUtc, 'days');

  if (diffInDays > 1) {
    return getLocaleSpecificTime(payInTime, 'YYYY-MM-DD hh:mm:ss', 'MMM DD');
  }
  return payInLabel;
};

const getPayoutLabel = (payOutTime: string, lastUpdated: string) => {
  const hoursDiff = moment(payOutTime)?.diff(lastUpdated, 'hours');
  if (hoursDiff <= 4)
    return getLocaleSpecificTime(payOutTime, 'YYYY-MM-DD hh:mm:ss', 'h:mm A, MMM DD');
  return getLocaleSpecificTime(payOutTime, 'YYYY-MM-DD hh:mm:ss', 'MMM DD');
};

const getTimelinePayInOrPayOutTime = (
  timelineData: GenericObject,
  timeline: TransferStatus[],
  transactionSummary: GenericObject
) => {
  const activeStateCode = timeline[0]?.state_code;

  if (timelineData?.rfi_initiated || timelineData?.sla_breached) return;
  else if (activeStateCode === TRANSFER_CREATED || activeStateCode === TRANSFER_FUNDED)
    return (
      <div className="dateContainer">
        {getPayInLabel(timelineData?.payin_time, timelineData?.payin_label)}
      </div>
    );
  else if (
    transactionSummary?.beneficiary_routing_config_type === CASH_PAYOUT &&
    activeStateCode !== PROCESSING
  )
    return;
  else if (activeStateCode === PAID) {
    return null;
  } else
    return (
      <div className="dateContainer">
        {getPayoutLabel(timelineData?.payout_time, timelineData?.last_updated)}
      </div>
    );
};

const getTimelineSubHeader = (
  timelineData: GenericObject,
  timelineCMSLabels: GenericObject,
  timeline: TransferStatus[],
  transactionSummary: GenericObject
) => {
  const activeStateCode = timeline[0]?.state_code;
  if (timelineData?.rfi_initiated) {
    if (timelineData.rfi_status === RFI_DRAFT)
      return <div className="subHeader">{timelineCMSLabels.awaitingAdditionalInfo}</div>;
    else {
      return (
        <div className="subHeader">
          {timelineCMSLabels.reviewingDocs}
          <Popup
            trigger={<img src={QUESTION_MARK_ICON} alt="info-icon" className="infoIcon" />}
            content={timelineCMSLabels.reviewingDocsToolTip}
            size="tiny"
            position="top center"
          />
        </div>
      );
    }
  }

  if (timelineData?.sla_breached) {
    return (
      <div className="subHeader">
        {timelineCMSLabels.txnBeachedDesc}
        <Popup
          trigger={<img src={QUESTION_MARK_ICON} alt="info-icon" className="infoIcon" />}
          content={timelineCMSLabels.txnBeachedToolTip}
          size="tiny"
          position={window.innerWidth <= 550 ? 'top left' : 'top center'}
        />
      </div>
    );
  }

  if (INCOMPLETE_REFUND_STATES.includes(activeStateCode)) {
    const refundTime = `${timelineData?.average_refund_processing_days || 1} - ${
      timelineData?.maximum_refund_processing_days || 2
    }`;
    return (
      <div className="subHeader">
        {timelineCMSLabels.refundSubHeader?.replace('{XX-XX}', refundTime)}
      </div>
    );
  } else if (activeStateCode === TRANSFER_CREATED || activeStateCode === TRANSFER_FUNDED) {
    return <div className="subHeader"> {timelineCMSLabels.shouldArrive} </div>;
  } else if (transactionSummary?.beneficiary_routing_config_type === CASH_PAYOUT) {
    if (activeStateCode === PROCESSING)
      return <div className="subHeader"> {timelineCMSLabels.shouldBeCollectedBy} </div>;
    else return;
  } else if (activeStateCode === PAID) {
    return null;
  } else {
    return <div className="subHeader"> {timelineCMSLabels.shouldPaidOutBy} </div>;
  }
};

const getTimelineActiveStateDescription = (
  item: any,
  timelineData: any,
  timelineCMSLabels: any,
  transactionSummary: any,
  onClose: any,
  isDeemedPaid: boolean
) => {
  const mapObj = {
    '{BENE_NAME}': transactionSummary?.beneficiary_first_name,
    '{TXN_NO}': timelineData?.transaction_number,
    '{AUTO_CANCELLATION}': transactionSummary?.auto_cancellation_hours
  };
  return (
    <div className="itemDescription">
      {isDeemedPaid
        ? DEEMED_PAID_TIMELINE_TEXT
        : (item.description ?? '').replace(
            /{BENE_NAME}|{TXN_NO}|{AUTO_CANCELLATION}/gi,
            function (matched: any) {
              //@ts-ignore
              return mapObj[matched];
            }
          )}
      {timelineData?.rfi_initiated && timelineData.rfi_status === RFI_DRAFT && (
        <>
          <div className="rfiError">{timelineCMSLabels.rfiDescription}</div>
          <Link to={RFI_URL} className="seeDetails" onClick={() => onClose(false)}>
            {timelineCMSLabels.rfiButton}
          </Link>
        </>
      )}
    </div>
  );
};

const TransactionTimeLine = (props: ITransactionTimeLine) => {
  const {
    timelineData,
    timeline,
    timelineCMSLabels,
    transactionSummary,
    onClose,
    context,
    handleRaiseNewTicket,
    handleOpenExistingTicketConversation,
    isTransactionNoPresentAndOpenFlag
  } = props;

  const {
    transaction_status_code,
    destination_currency,
    gross_source_amount,
    transaction_number,
    transaction_id
  } = transactionSummary;

  const isDeemedPaidSubStatus =
    transactionSummary?.pp_transaction_sub_status === PROCESSED_BY_CLEARING ||
    transactionSummary?.pp_transaction_sub_status === SENT_TO_BENEFICIARY_BANK;
  const isHelpCenterFlag = context?.props?.location?.pathname === HELP_CENTER;
  const timelineArrayLength = timeline.length;
  const activeStateCode = timeline[0]?.state_code;
  const lastUpdatedTime = getLocaleSpecificTime(
    timelineData?.last_updated,
    'YYYY-MM-DD hh:mm:ss',
    'h:mm A, MMM DD'
  );

  const handleTrackTransfer = () => {
    const data = {
      pageName: FLOW_TYPE.TRANSACTION_HISTORY,
      transactionId: transaction_id,
      transactionStatus: transaction_status_code
    };
    logEvent(EventNames.TRACK_TRANSFER, FLOW_TYPE.TRANSACTION_HISTORY, data, LOG_TYPE.CLEVER_TAP);
    onClose(false);
    context?.props?.navigate(`${HISTORY_URL}?transaction_no=${transaction_number}`);
  };

  const handleNewEnquiry = () => {
    const data = {
      pageName: FLOW_TYPE.TRANSACTION_HISTORY,
      transactionId: transaction_id,
      transactionStatus: transaction_status_code
    };
    logEvent(EventNames.NEW_ENQUIRY, FLOW_TYPE.TRANSACTION_HISTORY, data, LOG_TYPE.CLEVER_TAP);
    handleRaiseNewTicket();
    onClose();
  };

  const handleViewExistingTicket = () => {
    handleOpenExistingTicketConversation();
    onClose();
  };

  const transactionPaidModal = () => {
    return (
      <HelpSectionContainer id="transaction-paid-modal" width="100%">
        <img className="logo" src={PAID_TRANSACTION_ICON} alt="error-icon" width={60} />
        <p className="heading-bold">{`Your transaction ${transaction_number} is successful.`}</p>
        <p className="heading">
          {`Your payment of ${
            gross_source_amount + ' ' + destination_currency
          } has been successfuly transferred to the recipient. Please confirm
          with your recipient before raising a query.`}
        </p>

        <div id="view-transaction-details" className="btn-wrapper">
          <CommonButton
            text="View transaction details"
            width={418}
            height={46}
            onClick={handleTrackTransfer}
          />
        </div>
        <div id="new-enquiry" className="btn-wrapper">
          <CommonButton
            text="New enquiry"
            width={418}
            height={46}
            type="link"
            onClick={handleNewEnquiry}
          />
        </div>
      </HelpSectionContainer>
    );
  };

  const renderDeeplink = (onClose: (value: boolean) => void) => {
    if (transaction_status_code === AWAITING_PAYMENT || transaction_status_code === PROCESSING) {
      const key =
        transaction_status_code === AWAITING_PAYMENT
          ? 'timelineAwaitingWeb'
          : 'timelineInProgressWeb';

      return (
        <div className="deeplink-container">
          <HelpCenterDeepLink
            cmsKey={key}
            persistCurrentLevelItems={isHelpCenterFlag}
            onClose={onClose}
          />
        </div>
      );
    } else return null;
  };

  useEffect(() => {
    if (
      isHelpCenterFlag &&
      TRANSACTION_STAGES_NO_TIMELINE.includes(transactionSummary?.transaction_status_code) &&
      !isTransactionNoPresentAndOpenFlag
    ) {
      handleNewEnquiry();
    }
    //eslint-disable-next-line
  }, [transactionSummary?.transaction_status_code]);

  return (
    <>
      {isTransactionNoPresentAndOpenFlag ? (
        <ExistingTicketModal
          onClose={() => onClose(false)}
          handleViewTicket={handleViewExistingTicket}
        />
      ) : transaction_status_code === PAID && !isDeemedPaidSubStatus ? (
        <ModalContainer id="modal" width={500} mobileWidth={335}>
          <CloseModalIcon src={CLOSE_HEAVY_ICON} alt="close-modal" onClick={() => onClose(false)} />
          <ModalBodyContainer padding={[5, 24, 0, 24]}>{transactionPaidModal()}</ModalBodyContainer>
        </ModalContainer>
      ) : (
        <ModalContainer
          width={isHelpCenterFlag ? 500 : 520}
          mobileWidth={335}
          id="transaction-timeline"
        >
          <CloseModalIcon src={CLOSE_HEAVY_ICON} alt="close-modal" onClick={() => onClose(false)} />
          <ModalBodyContainer padding={isHelpCenterFlag ? [5, 24, 0, 24] : [5, 24, 60, 24]}>
            <div>
              {!isDeemedPaidSubStatus && (
                <ModalHeader>{timelineCMSLabels.transferTracker}</ModalHeader>
              )}
              {!isDeemedPaidSubStatus && (
                <ModalSubHeader>
                  {getTimelineSubHeader(
                    timelineData,
                    timelineCMSLabels,
                    timeline,
                    transactionSummary
                  )}
                  {!timelineData?.sla_breached &&
                    !REFUND_STATES.includes(activeStateCode) &&
                    getTimelinePayInOrPayOutTime(timelineData, timeline, transactionSummary)}
                </ModalSubHeader>
              )}
              <StatusContainer>
                {timeline?.map((item: any, index: number) => {
                  return (
                    <StatusItem key={index}>
                      <div className="leftDiv">
                        <img
                          src={
                            item.active
                              ? timelineData?.rfi_initiated && timelineData.rfi_status === RFI_DRAFT
                                ? TRANSACTION_TIMELINE_RFI_RAISED_ICON
                                : TRANSACTION_TIMELINE_ACTIVE_ICON
                              : TRANSACTION_TIMELINE_DISABLE_ICON
                          }
                          alt="Timeline Active"
                          className={item.active ? 'activeImg' : 'disabledImg'}
                        />
                        {item.active && timelineArrayLength !== index + 1 && (
                          <img
                            src={SOLID_VERTICAL_LINE_ICON}
                            alt="Vertical line"
                            className="solidVerticalLine"
                          />
                        )}
                        {timelineArrayLength !== index + 1 && (
                          <img
                            src={STROKED_VERTICAL_LINE_ICON}
                            alt="Vertical line"
                            className="verticalStrokedLine"
                          />
                        )}
                      </div>
                      <div className="rightDiv">
                        <div className="itemHeading">
                          <span
                            className={`firstColumn ${
                              item.active ? 'activeHeading' : 'disabledHeading'
                            } 
                          ${lastUpdatedTime.length !== 0 ? 'withOverflow' : ''} 
                          ${
                            PAY_IN_STATES.includes(activeStateCode) ||
                            item.state_code !== activeStateCode
                              ? 'width-100'
                              : ''
                          }
                        `}
                          >
                            {item.label}
                          </span>
                          {item.active &&
                            item.state_code !== TRANSFER_CREATED &&
                            item.state_code !== TRANSFER_FUNDED && (
                              <span className="secondColumn">
                                {item.state_code === AT_RECEPIENT_BANK ||
                                TRANSACTION_FINAL_STATES.includes(item.state_code)
                                  ? 'at '
                                  : 'since '}
                                {lastUpdatedTime}
                              </span>
                            )}
                        </div>
                        {item.active &&
                          getTimelineActiveStateDescription(
                            item,
                            timelineData,
                            timelineCMSLabels,
                            transactionSummary,
                            onClose,
                            item.state_code === PAID && isDeemedPaidSubStatus
                          )}
                        <div className="emptySpace" />
                      </div>
                    </StatusItem>
                  );
                })}

                {renderDeeplink(onClose)}
              </StatusContainer>
              {isHelpCenterFlag && (
                <HelpSectionContainer width="100%">
                  <p className="heading">
                    Your transfer is moving as expected and you can track it anytime.
                  </p>
                  <div>
                    <div className="btn-wrapper">
                      <CommonButton
                        text="Track Your transfer"
                        width={418}
                        height={46}
                        onClick={handleTrackTransfer}
                      />
                    </div>
                    <div className="btn-wrapper">
                      <CommonButton
                        text="New enquiry"
                        width={418}
                        height={46}
                        type="link"
                        onClick={() => handleNewEnquiry()}
                      />
                    </div>
                  </div>
                </HelpSectionContainer>
              )}
            </div>
          </ModalBodyContainer>
        </ModalContainer>
      )}
    </>
  );
};

export default TransactionTimeLine;
